// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
// Material UI Components
import { makeStyles } from '@material-ui/core/styles';
import { Fab, Menu, MenuItem, Tooltip } from '@material-ui/core';
import { CloudDownloadOutlined } from '@material-ui/icons';
import useWindowSize from '../../../customHooks/useWindowSize';
import NestedMenuItem from 'material-ui-nested-menu-item';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { CONSTANTS } from '../../../constants/constants';
import { connect } from 'react-redux';
import { getDefaultSchema } from '../../UploadHash/schema';
import * as XLSX from 'xlsx';

const DOCUMENT = 'document';
const FILE = 'file';
const BUNDLE = 'bundle';

const textDecoration = {
  textDecoration: 'none',
  color: 'inherit'
};

const useStyles = makeStyles(theme => ({
  fabExtended: {
    marginRight: theme.spacing(1)
  },
  menuItem: {
    textDecoration,
    '&:focus': textDecoration,
    '&:hover': textDecoration,
    '&:visited': textDecoration,
    '&:link': textDecoration,
    '&:active': textDecoration
  }
}));

const replaceQuotes = (toReplace) => {
  return toReplace.replace(/["]+/g, '\'');
};

const handleLink = (event, fileNameTemplate, callback) => {
  const date = moment().format('YYYYMMDD_HHmmss');
  event.currentTarget.attributes.download.nodeValue = fileNameTemplate.replace('date', date);
  callback();
};

const getDataKeysSchema = (schemaHash) => {
  if (schemaHash && schemaHash.schema && schemaHash.schema.properties) {
    const keys = Object.keys(schemaHash.schema.properties);
    const titles = keys.map(key => schemaHash.schema.properties[key].title);
    return { titles, keys };
  }
  return undefined;
};

const generateHeaders = (t, extended, schemaHash, type, isBundle, enableTags) => {
  const basicKeys = [
    CONSTANTS.CSV.NAME,
    CONSTANTS.CSV.DESCRIPTION,
    CONSTANTS.CSV.UPDATED_AT,
    CONSTANTS.CSV.FLOW_STATUS
  ];
  const extendedKeys = [
    CONSTANTS.CSV.FLOW_ID,
    CONSTANTS.CSV.TEMPLATE_ID,
    CONSTANTS.CSV.STEP_ID,
    CONSTANTS.CSV.STEP_TYPE,
    CONSTANTS.CSV.STEP_STATUS
  ];

  const hashKey = [
    CONSTANTS.CSV.HASH
  ];

  const subflowKeys = [
    CONSTANTS.CSV.NAME_SUBFLOW,
    CONSTANTS.CSV.DESCRIPTION_SUBFLOW,
    CONSTANTS.CSV.UPDATED_AT_SUBFLOW,
    CONSTANTS.CSV.FLOW_STATUS_SUBFLOW,
    CONSTANTS.CSV.FLOW_ID_SUBFLOW,
    CONSTANTS.CSV.TEMPLATE_ID_SUBFLOW,
    CONSTANTS.CSV.STEP_ID_SUBFLOW,
    CONSTANTS.CSV.STEP_TYPE_SUBFLOW,
    CONSTANTS.CSV.STEP_STATUS_SUBFLOW
  ];

  let keys = (extended ? basicKeys.concat(extendedKeys) : basicKeys);

  keys = (isBundle ? keys.concat(subflowKeys) : keys);

  keys = (type !== 'file' ? keys.concat(hashKey) : keys);

  keys = (enableTags ? keys.concat([CONSTANTS.CSV.TAGS]) : keys);

  let currentKeys = keys.map((key) => ({ key: key, label: t(`csvInfo:${key}`) }));

  let dataTitles = getDataKeysSchema(schemaHash);
  if (dataTitles) {
    currentKeys = keys.filter(key => ![CONSTANTS.CSV.NAME, CONSTANTS.CSV.DESCRIPTION].includes(key)).map((key) => ({ key: key, label: t(`csvInfo:${key}`) }));
    currentKeys = currentKeys.concat(dataTitles.keys.map((elem, index) => ({ key: elem, label: dataTitles.titles[index] })));
  }
  return currentKeys;
};

const generateCsvData = (t, extended, data, schemaHash, rowParent, enableTags) => {
  let csvResult = [];
  data && data.forEach((item) => {
    let documentData = {};
    documentData[CONSTANTS.CSV.NAME] = (item.name ? replaceQuotes(item.name) : '');
    documentData[CONSTANTS.CSV.DESCRIPTION] = (item.description ? replaceQuotes(item.description) : '');
    documentData[CONSTANTS.CSV.UPDATED_AT] = item.updatedAt;
    documentData[CONSTANTS.CSV.FLOW_STATUS] = t(`processDetail:${item.status}`);
    documentData[CONSTANTS.CSV.FLOW_ID] = item.flowId || '';

    if (enableTags) {
      documentData[CONSTANTS.CSV.TAGS] = item.tags ? item.tags.join('\n') : '';
    }

    let dataExtra = getDataKeysSchema(schemaHash);
    if (dataExtra && item.data) {
      let extraDataValues;
      delete documentData[CONSTANTS.CSV.NAME];
      delete documentData[CONSTANTS.CSV.DESCRIPTION];
      try {
        if (item.data) {
          extraDataValues = JSON.parse(item.data);
        }
      } catch (err) {
        console.log(err);
      }
      dataExtra.keys.forEach((key) => {
        documentData[key] = extraDataValues ? extraDataValues[key] : '';
      });
    }
    if (item.type !== 'file') {
      let hash;
      const flowForHash = typeof item.flow === 'string' ? JSON.parse(item.flow) : item.flow;
      if (flowForHash != null && flowForHash.steps != null) {
        const filteredSteps = flowForHash.steps.filter(e => e.type === 'transactSignStep' && e.status === 'signed');
        if (filteredSteps.length > 0 && filteredSteps[0] != null && filteredSteps[0].signResponse != null &&
          filteredSteps[0].signResponse.response != null) {
          if (filteredSteps[0].signResponse.response.length === 1 && filteredSteps[0].signResponse.response[0].hash != null) {
            hash = filteredSteps[0].signResponse.response[0].hash;
          } else if (filteredSteps[0].signResponse.response.hash != null) {
            hash = filteredSteps[0].signResponse.response.hash;
          }
        }
      }
      // const hash = JSON.parse(item?.flow)?.steps?.filter(e => e.type === 'transactSignStep' && e.status === 'signed')?.[0]?.signResponse?.response?.hash;
      documentData[CONSTANTS.CSV.HASH] = hash || '';
    }

    if (!extended || !item.flow) {
      csvResult.push(documentData);
    } else {
      const flow = typeof item.flow === 'string' ? JSON.parse(item.flow) : item.flow;
      documentData[CONSTANTS.CSV.TEMPLATE_ID] = flow.templateId;
      flow.steps.forEach((step) => {
        let flowData = {};
        flowData[CONSTANTS.CSV.STEP_ID] = step.stepId;
        flowData[CONSTANTS.CSV.STEP_TYPE] = t(`processDetail:stepType:${step.type}`);
        flowData[CONSTANTS.CSV.STEP_STATUS] = t(`processDetail:${step.status}`);

        const row = { ...rowParent, ...documentData, ...flowData };

        csvResult.push(row);
        if (step.type === 'subFlows' && item.document && item.document.items) {
          const subflowDocuments = item.document.items;

          const subflowRows = generateCsvData(t, extended, subflowDocuments, schemaHash, row, enableTags);

          const subflowRowCompleted = subflowRows.map((row) => {
            const renamed = Object.fromEntries(
              Object.entries(row).map(([key, value]) =>
                [`${key}_subflow`, value]
              )
            );

            return { ...row, ...renamed };
          });

          subflowRowCompleted.forEach((subflowRow) => {
            csvResult.push(subflowRow);
          });
        }
      });
    }
  });

  return csvResult;
};

const formatType = (type) => {
  switch (type) {
    case DOCUMENT: return 'Documents';
    case FILE: return 'Files';
    case BUNDLE: return 'Bundles';
    default: return '';
  }
};

const handleBasicDataCsv = (data, type) => {
  if (type === DOCUMENT && data && data.length === 1 && data[0].type === BUNDLE) {
    return [data[0], data[0].document.items].flat();
  } else {
    return data;
  }
};

const generateXlsx = (t, extended, schemaHash, enableTags, type, isBundle, data, rowParent) => {
  const headers = generateHeaders(t, extended, schemaHash, type, isBundle, enableTags);
  const csvData = generateCsvData(t, extended, data, schemaHash, rowParent, enableTags);
  const ws = XLSX.utils.json_to_sheet(csvData);
  for (let i = 0; i < headers.length; i++) {
    ws[XLSX.utils.encode_cell({ c: i, r: 0 })].v = headers[i].label;
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  const exportType = extended ? 'extended' : 'basic';
  const fType = formatType(type);
  const date = moment().format('YYYYMMDD_HHmmss');
  const fileName = `${exportType}${fType}_${date}.xlsx`;

  XLSX.writeFile(wb, fileName);
};

const CSVExport = (props) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  const { data, type, onlyBasic, config, isBundle } = props;
  const basicDataCsv = handleBasicDataCsv(data, type);
  const { extraConfig, enableTags } = config;
  let createHashSchema = getDefaultSchema();
  if (extraConfig && type === 'file') {
    const { createHash: customCreateHash } = JSON.parse(extraConfig.data);
    if (customCreateHash) {
      createHashSchema = customCreateHash;
    }
  }
  const classes = useStyles();
  const { t } = useTranslation();
  const windowSize = useWindowSize();

  const fType = formatType(type);
  const basicTemplateFN = `basic${fType}_date.csv`;
  const extendedTemplateFN = `extended${fType}_date.csv`;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const smallSize = windowSize.width < 760;
  const fabVariant = !smallSize ? 'extended' : 'circular';

  return (
    <>
      <Tooltip title={t('csvInfo:export')} enterDelay={200} enterTouchDelay={300} placement={'bottom'}>
        <Fab
          // className={classes.btnLink}
          color='secondary'
          aria-label='Export'
          variant={fabVariant}
          onClick={handleClick}
          id='export-button'
        >
          <CloudDownloadOutlined className={classes.fabExtended} />
          {!smallSize && t('csvInfo:export')}
        </Fab>
      </Tooltip>
      {open &&
        <Menu
          id='simple-menu'
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          disableScrollLock
          anchorReference='anchorPosition'
          anchorPosition={
            {
              top: anchorEl.getBoundingClientRect().bottom,
              left: anchorEl.getBoundingClientRect().left
            }
          }
        >
          <NestedMenuItem
            label={t('csvInfo:basic')}
            parentMenuOpen={!!anchorEl}
          >
            <CSVLink
              headers={generateHeaders(t, false, createHashSchema, type, false, enableTags)}
              data={generateCsvData(t, false, basicDataCsv, createHashSchema, false, enableTags)}
              filename={basicTemplateFN}
              onClick={(e) => handleLink(e, basicTemplateFN, handleClose)}
              className={classes.menuItem}
            >
              <MenuItem>
                {t('csvInfo:csv')}
              </MenuItem>
            </CSVLink>
            <MenuItem onClick={() => { generateXlsx(t, false, createHashSchema, enableTags, type, isBundle, data, false); }}>{t('csvInfo:xlsx')}</MenuItem>
          </NestedMenuItem>
          {!onlyBasic &&
          <NestedMenuItem
            label={t('csvInfo:extended')}
            parentMenuOpen={!!anchorEl}
          >
            <CSVLink
              headers={generateHeaders(t, true, createHashSchema, type, isBundle, enableTags)}
              data={generateCsvData(t, true, data, createHashSchema, false, enableTags)}
              filename={extendedTemplateFN}
              onClick={(e) => handleLink(e, extendedTemplateFN, handleClose)}
              className={classes.menuItem}
            >
              <MenuItem>
                {t('csvInfo:csv')}
              </MenuItem>
            </CSVLink>
            <MenuItem onClick={() => { generateXlsx(t, true, createHashSchema, enableTags, type, isBundle, data, false); }}>{t('csvInfo:xlsx')}</MenuItem>
          </NestedMenuItem>}
        </Menu>}
    </>
  );
};

CSVExport.propTypes = {
  data: PropTypes.array.isRequired,
  type: PropTypes.oneOf([DOCUMENT, FILE, BUNDLE]),
  config: PropTypes.object.isRequired,
  onlyBasic: PropTypes.bool,
  isBundle: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    config: state.config
  };
};

export default connect(mapStateToProps, null)(CSVExport);
